import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '../../../../components/Grid/Grid';
import Typography from '../../../../components/Typography/Typography';
import InputField from '../../../../components/InputField/InputField';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import SelectField from '../../../../components/SelectField/SelectField';
import useCancelToken from '../../../../hooks/useCancelToken';
import { getMaterialList } from '../../../../services/settings-config';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';

const CreatePilotTransferForm = ({
  formData,
  handleFormChange,
  formErr,
  machineList,
  setFormData,
  disabled,
}) => {
  const [materialList, setMaterialList] = useState([]);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [materialError, setMaterialError] = useState('');
  const { newCancelToken } = useCancelToken();

  const handleGetMaterialList = async () => {
    const cancelToken = newCancelToken();
    await getMaterialList({
      data: {},
      cancelToken,
      onSuccess: (result) => {
        setMaterialList(
          result.materialConfigList.map((item) => ({
            name: item.materialName,
            value: item.materialId,
          }))
        );
        setFormData((prev) => ({
          ...prev,
          materialName:
            result.materialConfigList.find((item) => item.materialName === prev.materialName)
              ?.materialName || '',
          materialId:
            result.materialConfigList.find((item) => item.materialName === prev.materialName)
              ?.materialId || '',
        }));
      },
      onError: (error) => {
        setMaterialError(error);
      },
      onSettled: () => {
        setMaterialLoading(false);
      },
    });
  };

  useEffect(() => {
    handleGetMaterialList();
  }, []);

  return (
    <Grid container direction="column" gap={3} sx={{ py: 1 }}>
      <Typography variant="subtitle1">請填製造資訊</Typography>
      <Grid item size={12}>
        <InputField
          id="bucketNo"
          label="桶號 / Nomor barel"
          value={formData.bucketNo}
          onChange={handleFormChange}
          placeholder="請輸入桶號 / Nomor barel"
          type="text"
          name="bucketNo"
          error={formErr.bucketNo}
          errormsg="請輸入正確的桶號 / Nomor barel"
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item size={12}>
        {materialLoading ? (
          <CircularProgress size={24} />
        ) : (
          <SelectField
            label="材質 / Bahan"
            placeholder="請選擇材質 / Bahan"
            id="materialId"
            value={formData.materialId}
            handleChange={(e) => {
              handleFormChange(e);
              setFormData((prev) => ({
                ...prev,
                materialName: materialList.find((item) => item.value === e.target.value)?.name,
              }));
            }}
            name="materialId"
            selections={materialList}
            error={formErr.materialId}
            errormsg="請選擇材質 / Bahan！"
            disabled={disabled || materialLoading}
          />
        )}
      </Grid>
      <Grid item size={12}>
        <InputField
          id="pilotTransferQty"
          label="試燒數量 / Kuantitas pembakaran"
          value={formData.pilotTransferQty}
          onChange={handleFormChange}
          placeholder="請輸入試燒數量 / Kuantitas pembakaran"
          type="number"
          name="pilotTransferQty"
          error={formErr.pilotTransferQty}
          errormsg="請輸入正確的試燒數量 / Kuantitas pembakaran"
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item size={12}>
        <SelectField
          label="壓型機台 / Mesin pembentuk"
          placeholder="請選擇壓型機台 / Mesin pembentuk"
          id="machineId"
          value={formData.machineId}
          handleChange={(e) => {
            handleFormChange(e);
            setFormData((prev) => ({
              ...prev,
              machineName: machineList.find((item) => item.value === e.target.value)?.name,
            }));
          }}
          name="machineId"
          selections={machineList}
          error={formErr.machineId}
          errormsg="請選擇壓型機台 / Mesin pembentuk！"
          disabled={disabled}
        />
      </Grid>
      <Grid item size={12}>
        <InputField
          id="remark"
          label="備註 / "
          value={formData.remark}
          onChange={handleFormChange}
          placeholder="請輸入備註 / "
          name="remark"
          error={formErr.remark}
          errormsg="請輸入正確的備註 / "
          fullWidth
          disabled={disabled}
        />
      </Grid>
      <Grid item size={12}>
        <Checkbox
          label="是否為PQC用試燒單？"
          checked={formData.isPQCPilot}
          onChange={(e) => setFormData((prev) => ({ ...prev, isPQCPilot: e.target.checked }))}
          name="isPQCPilot"
          error={formErr.isPQCPilot}
          errormsg="請選擇是否為PQC用試燒單？"
          reverse
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

CreatePilotTransferForm.propTypes = {
  formData: PropTypes.shape({
    bucketNo: PropTypes.string.isRequired,
    materialName: PropTypes.string.isRequired,
    materialId: PropTypes.string.isRequired,
    pilotTransferQty: PropTypes.string.isRequired,
    machineId: PropTypes.string.isRequired,
    isPQCPilot: PropTypes.bool.isRequired,
    remark: PropTypes.string.isRequired,
  }).isRequired,
  handleFormChange: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
  formErr: PropTypes.shape({
    bucketNo: PropTypes.bool.isRequired,
    materialName: PropTypes.bool.isRequired,
    materialId: PropTypes.bool.isRequired,
    pilotTransferQty: PropTypes.bool.isRequired,
    machineId: PropTypes.bool.isRequired,
    isPQCPilot: PropTypes.bool.isRequired,
    remark: PropTypes.bool.isRequired,
  }).isRequired,
  machineList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  disabled: PropTypes.bool.isRequired,
};

export default CreatePilotTransferForm;
